export type TransportErrorKind = 'cors-blocked' | 'dns' | 'network' | 'http' | 'rate-limited' | 'parse' | 'aborted';
export type FetcherKind = 'direct' | 'desktop' | 'proxy';

/** What the desktop shell's bridge functions reject with (a plain object, not an Error). */
export interface ShellError {
  kind: 'dns' | 'network' | 'aborted' | 'invalid' | 'internal';
  code?: number;
  message: string;
}

export function isShellError(e: unknown): e is ShellError {
  return typeof e === 'object' && e !== null && typeof (e as ShellError).kind === 'string' && typeof (e as ShellError).message === 'string';
}

export class TransportError extends Error {
  override name = 'TransportError';
  readonly kind: TransportErrorKind;
  readonly url: string;
  readonly status?: number;
  readonly retryAfterMs?: number;
  readonly via?: FetcherKind;
  /** First bytes of an error response body, when the server explained itself. */
  readonly detail?: string;

  constructor(
    kind: TransportErrorKind,
    message: string,
    opts: { url: string; status?: number; retryAfterMs?: number; via?: FetcherKind; cause?: unknown; detail?: string },
  ) {
    super(message, opts.cause !== undefined ? { cause: opts.cause } : undefined);
    this.kind = kind;
    this.url = opts.url;
    if (opts.status !== undefined) this.status = opts.status;
    if (opts.retryAfterMs !== undefined) this.retryAfterMs = opts.retryAfterMs;
    if (opts.via !== undefined) this.via = opts.via;
    if (opts.detail) this.detail = opts.detail;
  }

  /** Short human explanation with the usual remedy. */
  get hint(): string {
    switch (this.kind) {
      case 'cors-blocked':
        return 'The log does not allow browser requests (no CORS headers). Read it through a CORS proxy (Settings) or use the desktop app.';
      case 'rate-limited':
        return 'The log is rate limiting requests; the explorer will back off and retry.';
      case 'dns':
        return 'The host name could not be resolved (NXDOMAIN) or the server refused the connection — check the URL; the log may have been shut down.';
      case 'network':
        return 'Network error — check your connection or the log URL.';
      case 'aborted':
        return 'Request cancelled.';
      case 'parse':
        return 'The log returned data the explorer could not parse.';
      default:
        return this.detail ? `The server answered HTTP ${this.status ?? '?'}: ${this.detail}` : `The server answered HTTP ${this.status ?? '?'}.`;
    }
  }
}

/** Error messages from non-browser HTTP clients name the real cause. */
const DNS_PATTERN = /dns|resolve|lookup|name not known|nodename nor servname|ENOTFOUND|ECONNREFUSED|connection refused|failed to connect/i;

/**
 * Browsers report CORS rejections as an opaque TypeError, indistinguishable from a network
 * failure. Treat a TypeError while online as "probably CORS"; offline as a network error.
 * (The direct fetcher refines this with a no-cors probe — see `classifyDirectFailure`.)
 */
export function classifyFetchFailure(e: unknown, url: string, via: FetcherKind): TransportError {
  if (e instanceof TransportError) return e;
  if (e instanceof DOMException && e.name === 'AbortError') {
    return new TransportError('aborted', 'request aborted', { url, via, cause: e });
  }
  if (e instanceof Error && e.name === 'AbortError') {
    return new TransportError('aborted', 'request aborted', { url, via, cause: e });
  }
  const online = typeof navigator === 'undefined' || navigator.onLine !== false;
  if (via === 'desktop' && isShellError(e)) {
    const host = hostOf(url);
    switch (e.kind) {
      case 'dns':
        return new TransportError('dns', `cannot reach ${host}: ${e.message}`, { url, via, cause: e });
      case 'aborted':
        return new TransportError('aborted', 'request aborted', { url, via, cause: e });
      case 'invalid':
        return new TransportError('network', `invalid request for ${url}: ${e.message}`, { url, via, cause: e });
      default:
        return new TransportError('network', `${host}: ${e.message}`, { url, via, cause: e });
    }
  }
  if (via === 'desktop' && e instanceof Error && DNS_PATTERN.test(e.message)) {
    return new TransportError('dns', `cannot reach ${hostOf(url)}: ${e.message}`, { url, via, cause: e });
  }
  if (e instanceof TypeError && via === 'direct' && online) {
    return new TransportError('cors-blocked', `request to ${url} was blocked (likely missing CORS headers)`, { url, via, cause: e });
  }
  return new TransportError('network', e instanceof Error ? e.message : String(e), { url, via, cause: e });
}

function hostOf(url: string): string {
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
}

/** Parses a Retry-After header (delta-seconds or HTTP-date) into milliseconds from `now`. */
export function parseRetryAfter(value: string | null | undefined, now = Date.now()): number | undefined {
  if (!value) return undefined;
  const t = value.trim();
  if (/^\d+$/.test(t)) return Number(t) * 1000;
  const date = Date.parse(t);
  if (Number.isNaN(date)) return undefined;
  return Math.max(0, date - now);
}
